import { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { useClinic } from "@/context/ClinicContext";
import {
  canMarkArrived, canUndoArrival, isArrived, undoCountdown, undoSecondsLeft,
} from "@/lib/arrival";
import { Check, Loader2, UserCheck, UserX, Undo2, X } from "lucide-react";

/** Staff actions on one appointment: arrived, complete, no-show, cancel.
 *
 *  Shared by the /schedule day view and the patient profile, because those are the two
 *  places staff are actually looking when a visit ends, doesn't happen, or gets called
 *  off. Anywhere else and they'd be retyping into a search box to record something they
 *  are looking straight at.
 *
 *  Confirmation is inline, not window.confirm: a native dialog blocks the page, reads as
 *  a browser error, and cannot say WHICH appointment it is about.
 */
export default function AppointmentActions({ appt, onChanged, className = "" }) {
  /* The CLINIC's timezone, not the browser's. Found in a browser pass: the row renders
     `appt.time`, which is clinic-local, next to the arrival time — so on a Mac in
     Mountain Time looking at an Eastern clinic the row read "09:00 … Arrived 7:18 AM".
     Everything else on the schedule reads in the clinic's zone (see timeLabel() in
     Schedule.js) and this has to as well, or the two halves of one row disagree. */
  const { clinic } = useClinic();
  const tz = clinic?.timezone || undefined;

  const [mode, setMode] = useState("");        // "" | "cancel" | "complete" | "no_show"
  const [kind, setKind] = useState("staff_cancelled");
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const scheduled = appt.status === "scheduled";
  const requested = appt.status === "requested";

  /* A clock, only while there is something to count down. Arrival is the one action
     here with a deadline on it, and a tab left open must not keep offering an Undo the
     server has already stopped accepting. The interval exists for at most the length of
     the window and then stops itself. */
  const [now, setNow] = useState(() => Date.now());
  const counting = isArrived(appt) && undoSecondsLeft(appt, now) > 0;
  useEffect(() => {
    if (!counting) return;
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, [counting]);

  const arrived = isArrived(appt);
  const canUndo = canUndoArrival(appt, now);

  // Terminal statuses have nothing left to record. The backend agrees — completing an
  // already-cancelled visit is a 400 — so offering the button would only ever be a lie.
  if (!scheduled && !requested) return null;

  async function run(path, body) {
    setBusy(true); setError("");
    try {
      await api.post(`/appointments/${appt.id}/${path}`, body);
      setMode(""); setReason(""); setKind("staff_cancelled");
      onChanged?.();
    } catch (e) {
      setError(formatApiError(e.response?.data?.detail));
    } finally { setBusy(false); }
  }

  const reset = () => { setMode(""); setError(""); setReason(""); };

  return (
    <div className={`space-y-2 ${className}`} data-testid={`appt-actions-${appt.id}`}>
      {error && (
        <p className="text-xs text-destructive bg-destructive/10 border border-destructive/20 rounded px-2 py-1"
           data-testid={`appt-action-error-${appt.id}`}>{error}</p>
      )}

      {mode === "" && (
        <div className="flex flex-wrap items-center gap-1.5">
          {/* One click, no confirmation step. Somebody is standing at the desk, and the
              action is undoable for the next few minutes — which is a better answer to
              a misclick than a dialog in front of every correct click. */}
          {canMarkArrived(appt) && (
            <button data-testid={`staff-appt-arrived-${appt.id}`} disabled={busy}
                    onClick={() => run("arrived")}
                    className="inline-flex items-center gap-1 text-xs font-semibold border border-sky-300 text-sky-800 bg-sky-50 rounded-md px-2 py-1 hover:bg-sky-100 disabled:opacity-60">
              {busy ? <Loader2 className="w-3 h-3 animate-spin" /> : <UserCheck className="w-3 h-3" />}
              Arrived
            </button>
          )}
          {arrived && (
            <span className="inline-flex items-center gap-1 text-xs font-medium text-sky-800"
                  data-testid={`appt-arrived-at-${appt.id}`}>
              <UserCheck className="w-3 h-3" />
              Arrived {new Date(appt.arrived_at).toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit", timeZone: tz })}
            </span>
          )}
          {canUndo && (
            <button data-testid={`staff-appt-arrived-undo-${appt.id}`} disabled={busy}
                    onClick={() => run("arrived/undo")}
                    className="inline-flex items-center gap-1 text-xs font-medium border border-border rounded-md px-2 py-1 hover:bg-secondary disabled:opacity-60">
              <Undo2 className="w-3 h-3" /> Undo ({undoCountdown(appt, now)})
            </button>
          )}
          {scheduled && (
            <>
              <button data-testid={`staff-appt-complete-${appt.id}`} onClick={() => { setMode("complete"); setError(""); }}
                      className="inline-flex items-center gap-1 text-xs font-medium border border-border rounded-md px-2 py-1 hover:bg-secondary hover:border-emerald-300 hover:text-emerald-700 transition-colors">
                <Check className="w-3 h-3" /> Completed
              </button>
              <button data-testid={`staff-appt-no-show-${appt.id}`} onClick={() => { setMode("no_show"); setError(""); }}
                      className="inline-flex items-center gap-1 text-xs font-medium border border-border rounded-md px-2 py-1 hover:bg-secondary hover:border-amber-300 hover:text-amber-700 transition-colors">
                <UserX className="w-3 h-3" /> No-show
              </button>
            </>
          )}
          <button data-testid={`staff-appt-cancel-${appt.id}`} onClick={() => { setMode("cancel"); setError(""); }}
                  className="inline-flex items-center gap-1 text-xs font-medium border border-border rounded-md px-2 py-1 hover:bg-secondary hover:border-destructive hover:text-destructive transition-colors">
            <X className="w-3 h-3" /> Cancel
          </button>
        </div>
      )}

      {(mode === "complete" || mode === "no_show") && (
        <div className="flex flex-wrap items-center gap-2 border border-border rounded-md px-2 py-1.5 bg-secondary/40"
             data-testid={`appt-confirm-${appt.id}`}>
          <span className="text-xs">
            {mode === "complete" ? "Mark this visit completed?" : "Mark the patient as a no-show?"}
          </span>
          <button disabled={busy}
                  data-testid={`appt-confirm-yes-${appt.id}`}
                  onClick={() => run(mode === "complete" ? "complete" : "no-show")}
                  className="inline-flex items-center gap-1 text-xs font-semibold bg-primary text-primary-foreground rounded-md px-2 py-1 disabled:opacity-60">
            {busy ? <Loader2 className="w-3 h-3 animate-spin" /> : null} Yes
          </button>
          <button onClick={reset} className="text-xs text-muted-foreground hover:text-foreground">No</button>
        </div>
      )}

      {mode === "cancel" && (
        <div className="border border-border rounded-md p-2.5 bg-secondary/40 space-y-2"
             data-testid={`appt-cancel-form-${appt.id}`}>
          {/* Who cancelled is a structured field, not a note: the waitlist and every
              cancellation metric branch on it. */}
          <div className="space-y-1">
            {[["staff_cancelled", "The clinic is cancelling"],
              ["patient_cancelled", "The patient asked to cancel"]].map(([v, label]) => (
              <label key={v} className="flex items-center gap-2 text-xs cursor-pointer">
                <input type="radio" name={`cancel-kind-${appt.id}`} value={v}
                       data-testid={`cancel-kind-${v}-${appt.id}`}
                       checked={kind === v} onChange={() => setKind(v)}
                       className="w-3.5 h-3.5" />
                {label}
              </label>
            ))}
          </div>
          <input data-testid={`cancel-reason-${appt.id}`} value={reason}
                 onChange={(e) => setReason(e.target.value)}
                 placeholder="Reason (optional)"
                 className="w-full border border-input bg-card rounded-md px-2 py-1 text-xs outline-none focus:ring-2 focus:ring-ring" />
          <div className="flex items-center gap-2">
            <button disabled={busy} data-testid={`cancel-confirm-${appt.id}`}
                    onClick={() => run("cancel", { reason, cancellation_kind: kind })}
                    className="inline-flex items-center gap-1 text-xs font-semibold bg-destructive text-destructive-foreground rounded-md px-2 py-1 disabled:opacity-60">
              {busy ? <Loader2 className="w-3 h-3 animate-spin" /> : null} Cancel the visit
            </button>
            <button onClick={reset} className="text-xs text-muted-foreground hover:text-foreground">Keep it</button>
          </div>
        </div>
      )}
    </div>
  );
}
